import { useTranslation } from "react-i18next";
import { usePageSEO } from "@/lib/use-page-seo";
import { useLocalePath } from "@/lib/use-locale-path";
import WCLogo from "@/components/WCLogo";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import MegaFooter from "@/components/MegaFooter";
import { WC2026_TEAMS } from "../../../server/teams";
import { GROUP_ROUTES, slugifyTeam, teamPath, groupPath } from "../../../shared/seo-routes";
import {
  groupStageFixtures,
  formatFixtureDateTime,
} from "@/lib/fixtures-for-seo";
import type { SupportedLanguage } from "@/i18n";

interface Props {
  params: { letter?: string };
}

export default function GroupPage({ params }: Props) {
  const { t, i18n } = useTranslation();
  const lp = useLocalePath();
  const letter = (params.letter ?? "").toUpperCase();
  const lang = (i18n.language || "en").slice(0, 2);
  const route = GROUP_ROUTES.find((r) => r.letter === letter);
  const teams = route ? WC2026_TEAMS.filter((tm) => tm.group === letter) : [];
  const availableLanguages: SupportedLanguage[] = route
    ? ["en", ...route.localizedLocales]
    : ["en"];

  usePageSEO("seo.group.title", groupPath(letter.toLowerCase()));

  if (!route) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background text-foreground">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-2">{t("not_found.heading")}</h1>
          <a href={lp("/groups")} className="text-primary hover:underline">{t("seo.group.index_h1")}</a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="border-b border-border/40 bg-background/80">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 flex items-center justify-between h-14">
          <WCLogo />
          <div className="flex items-center gap-4">
            <a href={lp("/")} className="text-sm text-muted-foreground hover:text-foreground">
              {t("nav.back")}
            </a>
            <LanguageSwitcher availableLanguages={availableLanguages} />
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-12">
        <nav aria-label="Breadcrumb" className="text-xs text-muted-foreground mb-6">
          <a href={lp("/")} className="hover:underline">{t("seo.common.home")}</a>
          <span className="mx-2">/</span>
          <a href={lp("/groups")} className="hover:underline">{t("seo.group.index_h1")}</a>
          <span className="mx-2">/</span>
          <span className="text-foreground">{t("seo.team.group_label", { group: letter })}</span>
        </nav>

        <h1 className="text-4xl sm:text-5xl font-extrabold uppercase tracking-tight mb-3"
            style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
          {t("seo.group.h1", { group: letter })}
        </h1>
        <p className="text-base sm:text-lg text-muted-foreground max-w-2xl mb-4">
          {t("seo.group.intro", { group: letter })}
        </p>
        <p className="text-sm text-muted-foreground max-w-2xl mb-10">
          {t("seo.group.detail_body", { group: letter })}
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-10">
          {teams.map((team) => (
            <a key={team.code}
               href={lp(teamPath(slugifyTeam(team.name)))}
               className="flex items-center gap-4 rounded-md border border-border/50 px-4 py-3 hover:bg-muted/30 hover:border-border transition-colors">
              <span className="text-4xl" aria-hidden="true">{team.flag}</span>
              <div className="flex-1 min-w-0">
                <div className="font-medium">{team.name}</div>
                <div className="text-xs text-muted-foreground">{team.confederation}</div>
              </div>
            </a>
          ))}
        </div>

        <section className="mb-10">
          <h2 className="text-2xl font-bold uppercase mb-4"
              style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
            {t("seo.group.fixtures_h2", { group: letter })}
          </h2>
          {(() => {
            const fixtures = groupStageFixtures(letter);
            if (fixtures.length === 0) {
              return <p className="text-sm text-muted-foreground">{t("seo.fixtures.tbd")}</p>;
            }
            return (
              <ul className="space-y-2">
                {fixtures.map((f) => (
                  <li key={f.id}
                      className="rounded-md border border-border/50 px-4 py-3 text-sm flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{f.homeTeam.name}</span>
                      <span aria-hidden="true" className="text-base">{f.homeTeam.flag}</span>
                      <span className="text-muted-foreground">vs</span>
                      <span aria-hidden="true" className="text-base">{f.awayTeam.flag}</span>
                      <span className="font-medium">{f.awayTeam.name}</span>
                    </div>
                    <div className="text-xs text-muted-foreground flex flex-wrap gap-x-2 gap-y-1">
                      <span>{formatFixtureDateTime(lang, f.kickoffIso)}</span>
                      <span>·</span>
                      <span>{f.venue}, {f.city}</span>
                    </div>
                  </li>
                ))}
              </ul>
            );
          })()}
        </section>

        <section className="mb-10">
          <h2 className="text-2xl font-bold uppercase mb-4"
              style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
            {t("seo.group.format_h2")}
          </h2>
          <p className="text-sm text-muted-foreground">
            {t("seo.group.format_body")}
          </p>
        </section>

        <div className="flex flex-wrap gap-3">
          <a href={lp("/")}
             className="inline-flex items-center px-5 py-2.5 rounded-md bg-primary text-primary-foreground font-medium text-sm hover:opacity-90">
            {t("seo.common.cta_pick_teams")}
          </a>
          <a href={lp("/groups")}
             className="inline-flex items-center px-5 py-2.5 rounded-md border border-border text-sm hover:bg-muted/30">
            {t("seo.group.index_h1")}
          </a>
        </div>
      </main>

      <MegaFooter />
    </div>
  );
}
